import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const PaymentMethodSelector = ({ selectedMethod, onMethodChange, paymentDetails, onPaymentDetailsChange }) => {
  const paymentMethods = [
    {
      id: 'gcash',
      name: 'GCash',
      icon: 'Smartphone',
      description: 'Instant payment via GCash wallet',
      processingTime: 'Instant confirmation',
      fee: 'No additional fees',
      recommended: true
    },
    {
      id: 'pay_at_hotel',
      name: 'Pay at Hotel',
      icon: 'Building',
      description: 'Pay when you arrive at the property',
      processingTime: 'Confirmed upon arrival',
      fee: 'No processing fees',
      recommended: false
    },
    {
      id: 'credit_card',
      name: 'Credit/Debit Card',
      icon: 'CreditCard',
      description: 'Visa, Mastercard, JCB accepted',
      processingTime: 'Instant confirmation',
      fee: '2.5% processing fee',
      recommended: false
    }
  ];

  const handlePaymentDetailChange = (field, value) => {
    onPaymentDetailsChange({
      ...paymentDetails,
      [field]: value
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Icon name="CreditCard" size={24} className="text-primary" />
          Payment Method
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Select your preferred payment option</p>
      </div>

      {/* Payment Methods */}
      <div className="p-6 space-y-3">
        {paymentMethods?.map((method) => (
          <button
            key={method?.id}
            onClick={() => onMethodChange(method?.id)}
            className={`w-full flex items-start justify-between p-4 rounded-lg border-2 transition-all ${
              selectedMethod === method?.id
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted'
            }`}
          >
            <div className="flex items-start gap-4">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  selectedMethod === method?.id ? 'bg-primary/20' : 'bg-muted'
                }`}
              >
                <Icon
                  name={method?.icon}
                  size={24}
                  className={selectedMethod === method?.id ? 'text-primary' : 'text-foreground'}
                />
              </div>
              <div className="text-left flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-foreground">{method?.name}</h4>
                  {method?.recommended && (
                    <span className="px-2 py-0.5 rounded-full bg-success text-success-foreground text-xs font-semibold">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">{method?.description}</p>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Clock" size={12} />
                    <span>{method?.processingTime}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="DollarSign" size={12} />
                    <span>{method?.fee}</span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                selectedMethod === method?.id
                  ? 'border-primary bg-primary' :'border-muted-foreground'
              }`}
            >
              {selectedMethod === method?.id && <div className="w-2 h-2 rounded-full bg-white" />}
            </div>
          </button>
        ))}
      </div>

      {/* Payment Details Forms */}
      <div className="px-6 pb-6">
        {/* GCash Form */}
        {selectedMethod === 'gcash' && (
          <div className="space-y-4 p-4 bg-muted/50 rounded-xl border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Smartphone" size={20} className="text-primary" />
              <h4 className="font-semibold text-foreground">GCash Payment Details</h4>
            </div>
            <Input
              type="tel"
              label="GCash Mobile Number"
              placeholder="09XX XXX XXXX"
              value={paymentDetails?.gcashNumber || ''}
              onChange={(e) => handlePaymentDetailChange('gcashNumber', e?.target?.value)}
              required
            />
            <Input
              type="text"
              label="Account Name"
              placeholder="Full name as registered in GCash"
              value={paymentDetails?.gcashName || ''}
              onChange={(e) => handlePaymentDetailChange('gcashName', e?.target?.value)}
              required
            />
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <p className="text-xs text-muted-foreground">
                You will receive a payment request on your GCash app. Please approve within 10 minutes to confirm your booking.
              </p>
            </div>
          </div>
        )}

        {/* Pay at Hotel Form */}
        {selectedMethod === 'pay_at_hotel' && (
          <div className="space-y-4 p-4 bg-muted/50 rounded-xl border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Building" size={20} className="text-primary" />
              <h4 className="font-semibold text-foreground">Pay at Hotel Instructions</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <p className="text-sm text-foreground">Your booking will be confirmed immediately</p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <p className="text-sm text-foreground">Payment is due upon check-in at the property</p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <p className="text-sm text-foreground">Accepted payment methods: Cash, Credit Card, Debit Card</p>
              </div>
            </div>
            <div className="p-3 bg-warning/10 rounded-lg border border-warning/20">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Please note: The property may require a valid credit card or cash deposit upon check-in for incidentals.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Credit Card Form */}
        {selectedMethod === 'credit_card' && (
          <div className="space-y-4 p-4 bg-muted/50 rounded-xl border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CreditCard" size={20} className="text-primary" />
              <h4 className="font-semibold text-foreground">Credit Card Details</h4>
            </div>
            <Input
              type="text"
              label="Card Number"
              placeholder="1234 5678 9012 3456"
              value={paymentDetails?.cardNumber || ''}
              onChange={(e) => handlePaymentDetailChange('cardNumber', e?.target?.value)}
              required
            />
            <Input
              type="text"
              label="Cardholder Name"
              placeholder="Name as shown on card"
              value={paymentDetails?.cardName || ''}
              onChange={(e) => handlePaymentDetailChange('cardName', e?.target?.value)}
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="text"
                label="Expiry Date"
                placeholder="MM/YY"
                value={paymentDetails?.cardExpiry || ''}
                onChange={(e) => handlePaymentDetailChange('cardExpiry', e?.target?.value)}
                required
              />
              <Input
                type="text"
                label="CVV"
                placeholder="123"
                value={paymentDetails?.cardCvv || ''}
                onChange={(e) => handlePaymentDetailChange('cardCvv', e?.target?.value)}
                required
              />
            </div>
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-2">
                <Icon name="Lock" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Your payment information is encrypted and secure. We use industry-standard SSL encryption.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodSelector;